import { api } from '@/lib/api';

// Types
export interface Recipe {
    id: string;
    name: string;
    imageUrl?: string;
    description?: string;
    prepTime?: number;
    cookTime?: number;
    servings?: number;
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
}

export interface ProgramMeal {
    id: string;
    mealType: 'BREAKFAST' | 'LUNCH' | 'DINNER' | 'SNACK';
    servings: number;
    notes?: string;
    recipeId: string;
    recipe: Recipe;
}

export interface ProgramDay {
    dayNumber: number;
    title?: string;
    description?: string;
    meals: ProgramMeal[];
}

export interface Program {
    id: string;
    name: string;
    description?: string;
    duration: number;
    imageUrl?: string;
    isActive: boolean;
    days: ProgramDay[];
}

export interface ProgramAssignment {
    id: string;
    programId: string;
    clientId: string;
    startDate: string;
    status: 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';
    notes?: string;
    program: Program;
}

// Menu meal from backend - includes meal data directly (not nested under 'meal')
export interface MenuMeal extends ProgramMeal {
    // Discriminator the backend tags every meal with. Drives which completion
    // endpoint the client hits. Defaults treated as 'program' on the few code
    // paths that don't read it explicitly.
    type?: 'program' | 'custom';
    scheduledDate: string;
    isCompleted: boolean;
    completedAt?: string;
    /**
     * Servings one person eats when they tick the meal, which is what points
     * scale with: the coach's portion, otherwise 1. `servings` is how much to
     * cook and is mostly the recipe's yield (#394).
     */
    servingsEaten?: number;
    servingsAdjusted?: number;
    // Customization fields
    isSkipped?: boolean;
    isSubstituted?: boolean;
    isCustomMeal?: boolean;
    coachNotes?: string;
    coachServings?: number;
    originalRecipe?: Recipe; // For substituted meals, the original recipe
}

export interface DailyMenu {
    date: string;
    dayNumber: number;
    dayName?: string;
    meals: MenuMeal[];
}

interface MyMenuResponse {
    program: {
        id: string;
        name: string;
        duration: number;
    };
    assignmentId: string;
    startDate: string;
    menu: DailyMenu[];
}

// API functions
export const getMyProgram = async (): Promise<ProgramAssignment | null> => {
    try {
        const response = await api.get('/programs/my-program');
        return response.data;
    } catch (error: unknown) {
        const err = error as { response?: { status: number } };
        if (err.response?.status === 404) {
            return null;
        }
        throw error;
    }
};

export const getMyMenu = async (startDate: string, endDate: string): Promise<DailyMenu[]> => {
    const response = await api.get<MyMenuResponse>('/programs/my-menu', {
        params: { startDate, endDate },
    });
    // Backend returns { program, assignmentId, startDate, menu }
    return response.data.menu || [];
};

export interface MarkMealCompleteInput {
    mealId: string;
    // Discriminator: program-meal completion needs the scheduled date (because
    // the same programMealId repeats across the program duration), custom-meal
    // completion looks the date up off the row itself.
    mealType: 'program' | 'custom';
    scheduledDate: string;
    isCompleted: boolean;
}

export const markMealComplete = async ({
    mealId,
    mealType,
    scheduledDate,
    isCompleted,
}: MarkMealCompleteInput): Promise<{ pointsEarned: number | null }> => {
    if (mealType === 'custom') {
        const response = await api.patch<{ pointsEarned: number | null }>(
            `/programs/my-menu/custom/${mealId}/complete`,
            { isCompleted },
        );
        return { pointsEarned: response.data.pointsEarned ?? null };
    }

    const response = await api.patch<{ pointsEarned: number | null }>(
        `/programs/my-menu/${mealId}/complete`,
        { isCompleted },
        {
            params: { date: scheduledDate },
        },
    );
    return { pointsEarned: response.data.pointsEarned ?? null };
};

export const adjustServings = async (
    mealId: string,
    scheduledDate: string,
    servings: number,
): Promise<void> => {
    await api.patch(
        `/programs/my-menu/${mealId}/servings`,
        { servings },
        {
            params: { date: scheduledDate },
        },
    );
};

// React Query hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateMealData, invalidateMenuData } from '@/lib/mealDataQueries';

export const useMyProgram = () => {
    return useQuery({
        queryKey: ['my-program'],
        queryFn: getMyProgram,
    });
};

export const useMyMenu = (startDate: string, endDate: string) => {
    return useQuery({
        queryKey: ['my-menu', startDate, endDate],
        queryFn: () => getMyMenu(startDate, endDate),
        enabled: !!startDate && !!endDate,
    });
};

export const useMarkMealComplete = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: MarkMealCompleteInput) => markMealComplete(input),
        // Ticking writes (and unticking deletes) a MealLog and re-scores the
        // day, so Journal, Meals and the dashboard's points and streak change
        // too, not just the menu. Returned so mutateAsync settles after the
        // mounted queries have refetched.
        onSuccess: () => invalidateMealData(queryClient),
    });
};

export const useAdjustServings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            mealId,
            scheduledDate,
            servings,
        }: {
            mealId: string;
            scheduledDate: string;
            servings: number;
        }) => adjustServings(mealId, scheduledDate, servings),
        // Cooking servings only: points are scored on servings eaten, so the
        // meal logs are unchanged and only the menu views need a refresh.
        onSuccess: () => invalidateMenuData(queryClient),
    });
};

// Shopping list generation from program menu
export interface ShoppingListItem {
    id: string;
    ingredientId: string;
    amount: number;
    unit: string;
    section?: string;
    ingredient: {
        id: string;
        name: string;
        category?: string;
    };
}

export interface ShoppingList {
    id: string;
    name: string;
    items: ShoppingListItem[];
}

export interface MealServingOverride {
    mealId: string;
    servings: number;
}

export const generateShoppingListFromMenu = async (
    startDate: string,
    endDate: string,
    name?: string,
    mealIds?: string[], // Optional: if provided, only include these specific meals
    mealServings?: MealServingOverride[], // Optional: override servings per meal
    deductFromPantry?: boolean, // Optional: if true, subtract pantry quantities
): Promise<ShoppingList> => {
    const response = await api.post('/programs/my-menu/shopping-list', {
        startDate,
        endDate,
        name,
        mealIds,
        mealServings,
        deductFromPantry,
    });
    return response.data;
};

export const useGenerateShoppingList = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            startDate,
            endDate,
            name,
            mealIds,
            mealServings,
            deductFromPantry,
        }: {
            startDate: string;
            endDate: string;
            name?: string;
            mealIds?: string[]; // Optional: if provided, only include these specific meals
            mealServings?: MealServingOverride[]; // Optional: override servings per meal
            deductFromPantry?: boolean; // Optional: if true, subtract pantry quantities
        }) =>
            generateShoppingListFromMenu(
                startDate,
                endDate,
                name,
                mealIds,
                mealServings,
                deductFromPantry,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
        },
    });
};
